import React from "react";
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { Button } from "components";
import { Row } from "reactstrap";
import { notificationMessageActions } from "_actions";
import { imageConstants } from "_constants";

import defaultImage from "assets/img/event_image_placeholder.jpg";
import setCanvasPreview from "./setCanvasPreview";

const MIN_DIMENSION = 150;          // px minimum along smallest side
const MAX_IMAGE_BYTES = 7_500_000;  // 7.5MB

class PostUpload extends React.Component {
  // Stable refs (never keep refs in state)
  fileInputRef = React.createRef();
  imgRef = React.createRef();
  previewCanvasRef = React.createRef();

  constructor(props) {
    super(props);
    const aspectRatio = props.aspectRatio ? props.aspectRatio : imageConstants.squareAspectRatio
    this.state = {
      file: null,
      blob: null,
      croppedImageUrl: null,

      imagePreviewUrl:
        props.imageUrl && props.imageUrl.length > 0
          ? props.imageUrl
          : defaultImage,

      index: props.index !== undefined ? props.index : null,

      // cropping state
      src: null,             // dataURL for cropper
      imgSrc: "",            // same as src; kept for compatibility
      cropOpen: false,
      aspectRatio: aspectRatio,
      crop: { aspect: aspectRatio, width: 50, x: 0, y: 0 },

      error: "",
    };
  }

  // ---------- helpers ----------
  readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = () => resolve(String(reader.result || ""));
      reader.readAsDataURL(file);
    });
  }

  ensureMinDimensions(dataUrl, minW, minH) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth >= minW && img.naturalHeight >= minH) resolve();
        else reject(new Error(`Image must be at least ${minW} x ${minH} pixels.`));
      };
      img.onerror = () => reject(new Error("Unable to load image"));
      img.src = dataUrl;
    });
  }

  canvasToBlob(canvas, type = "image/png", quality = 0.92) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("canvas.toBlob() returned null"));
        }, type, quality);
      } catch (e) {
        reject(e);
      }
    });
  }

  // ---------- actions ----------
  handleClick = () => {
    this.fileInputRef.current?.click();
    this.setState({ cropOpen: true });
  };

  onSelectFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      this.props.dispatch?.(
        notificationMessageActions.addNotificationToQueue(
          "Please select a JPG or PNG image.",
          2
        )
      );
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      this.props.dispatch?.(
        notificationMessageActions.addNotificationToQueue(
          "Your post image must be less than 7.5 MB.",
          2
        )
      );
      return;
    }

    try {
      const dataUrl = await this.readFileAsDataURL(file);
      await this.ensureMinDimensions(dataUrl, MIN_DIMENSION, MIN_DIMENSION);

      this.setState({
        file,
        src: dataUrl,
        imgSrc: dataUrl,
        croppedImageUrl: null,
        blob: null,
        error: "",
        cropOpen: true,
      });
    } catch (err) {
      const msg = err?.message || "Invalid image selected.";
      this.setState({ error: msg, src: null, imgSrc: "", cropOpen: false });
      this.props.dispatch?.(
        notificationMessageActions.addNotificationToQueue(msg, 2)
      );
    }
  };

  onImageLoad = (e) => {
    const { aspectRatio } = this.state
    const img = e.currentTarget;
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    // Make initial % width large enough to satisfy MIN_DIMENSION in px
    const cropWidthPct = Math.min(100, Math.max((MIN_DIMENSION / width) * 100, 1));

    const base = makeAspectCrop(
      { unit: "%", width: cropWidthPct },
      aspectRatio,
      width,
      height
    );
    const centered = centerCrop(base, width, height);
    this.setState({ crop: centered });
  };

  performCrop = async () => {
    const img = this.imgRef.current;
    const canvas = this.previewCanvasRef.current;
    const { crop } = this.state;
    if (!img || !canvas || !crop) return;

    // Accurate pixel crop using NATURAL dimensions
    const pixelCrop = convertToPixelCrop(
      crop,
      img.naturalWidth,
      img.naturalHeight
    );

    try {
      setCanvasPreview(img, canvas, pixelCrop /*, { backgroundColor: "#fff" }*/);
      const dataUrl = canvas.toDataURL("image/png");
      const blob = await this.canvasToBlob(canvas, "image/png", 0.92);

      this.updateAvatar(dataUrl, blob); // will call handleImageChange to finalize
    } catch (e) {
      console.error("Crop failed:", e);
      const dataUrl = canvas.toDataURL("image/png");
      this.updateAvatar(dataUrl, null);
    }
  };

  updateAvatar = (imgSrc, blob) => {
    this.setState({ croppedImageUrl: imgSrc, blob }, () => this.handleImageChange());
  };

  handleImageChange = () => {
    const { blob, croppedImageUrl } = this.state;
    if (!croppedImageUrl) return;

    // Update main preview and notify parent
    this.setState(
      {
        imagePreviewUrl: croppedImageUrl,
        src: null,
        imgSrc: "",
        cropOpen: false,
      },
      () => {
        this.props.callback?.({ state: this.state, newImage: true });
      }
    );
  };

  render() {
    const {
      crop,
      croppedImageUrl,
      imgSrc,
      imagePreviewUrl,
      error,
      aspectRatio,
    } = this.state;

    const prefersDark = localStorage.getItem("darkMode") === "true";

    // UI flags to avoid duplicate buttons
    const isCropping = !!imgSrc && !croppedImageUrl;
    const hasSavedImage = !!croppedImageUrl || (imagePreviewUrl && imagePreviewUrl !== defaultImage);

    return (
      <div className="fileinput text-center full-width">
        {/* Hidden file input */}
        <input
          type="file"
          accept=".jpg,.jpeg,.png"
          onChange={this.onSelectFile}
          ref={this.fileInputRef}
          style={{ display: "none" }}
        />

        {/* Preview */}
        <div className="thumbnail">
          {!croppedImageUrl && (
            <img width="240" height={240 / aspectRatio} src={imagePreviewUrl} alt="Post" />
          )}
          {croppedImageUrl && (
            <img width="240" height={240 / aspectRatio} alt="Crop" src={croppedImageUrl} />
          )}
        </div>

        {/* Error */}
        {error ? <div className="text-danger small mt-2">{error}</div> : null}

        {/* Cropper */}
        {isCropping && (
          <div className="text-center mb-3">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => this.setState({ crop: percentCrop })}
              keepSelection
              aspect={aspectRatio}
              minWidth={MIN_DIMENSION}
            >
              <img
                ref={this.imgRef}
                src={imgSrc}
                alt="Upload"
                style={{ maxHeight: "70vh" }}
                onLoad={this.onImageLoad}
              />
            </ReactCrop>

            <Row className="text-center mx-auto pt-2">
              <Button
                size="sm"
                color="success"
                className="mx-auto"
                onClick={this.performCrop}
              >
                Confirm Image
              </Button>
            </Row>

            {crop && (
              <canvas
                ref={this.previewCanvasRef}
                className="mt-4"
                style={{
                  display: "none",
                  border: "1px solid black",
                  objectFit: "contain",
                  width: 150,
                  height: 150,
                }}
              />
            )}
          </div>
        )}

        {/* Actions — never show duplicate buttons */}
        <div className="mt-1">
          {(() => {
            if (!hasSavedImage && !isCropping) {
              // No image yet → Add
              return (
                <Button
                  className="btn-primary"
                  simple
                  size="sm"
                  outline
                  color="primary"
                  onClick={this.handleClick}
                >
                  Add Image
                </Button>
              );
            }
            if (isCropping) {
              // While cropping → only "Choose Different File"
              return (
                <Button
                  size="sm"
                  color={prefersDark ? "spc-white" : "default"}
                  onClick={this.handleClick}
                >
                  Choose Different File
                </Button>
              );
            }
            // Image saved / showing → Update
            return (
              <Button
                className="btn-primary"
                simple
                size="sm"
                outline
                color="primary"
                onClick={this.handleClick}
              >
                Update Image
              </Button>
            );
          })()}
        </div>
      </div>
    );
  }
}

export default PostUpload;